import { create } from "zustand";
import { persist } from "zustand/middleware";
import { type AuthUser, useAuthStore } from "@/features/auth/store/auth-store";
import * as opsApi from "@/features/ops/api";
import type { ImpersonateResult } from "@/features/ops/types";

/** The operator's real session, stashed while impersonating so "return to
 * yourself" can restore it exactly. */
interface Origin {
  user: AuthUser;
  accessToken: string;
  refreshToken: string;
}

interface ImpersonationState {
  origin: Origin | null;
  targetName: string | null;
  isImpersonating: boolean;
  /** Swap the active auth session to the target; stash the operator's. */
  begin: (result: ImpersonateResult) => void;
  /** Restore the operator's session and close the server-side session. */
  end: () => Promise<void>;
  /** Drop all impersonation state (on logout / forced session clear). */
  reset: () => void;
}

// NOTE: this store persists the operator's origin tokens so "Return to yourself"
// survives a page reload mid-impersonation (same in-localStorage-tokens tradeoff
// the auth store already makes). It is reset the instant the real session clears
// (see the auth-store subscription below), so a stale impersonation can never
// bleed into a *different* user's next login on the same browser.
export const useImpersonationStore = create<ImpersonationState>()(
  persist(
    (set, get) => ({
      origin: null,
      targetName: null,
      isImpersonating: false,
      reset: () => set({ origin: null, targetName: null, isImpersonating: false }),
      begin: (result) => {
        const auth = useAuthStore.getState();
        // Only capture the origin on the FIRST hop — never overwrite the real
        // operator session with an already-impersonated one.
        const origin =
          get().origin ??
          (auth.user && auth.accessToken && auth.refreshToken
            ? { user: auth.user, accessToken: auth.accessToken, refreshToken: auth.refreshToken }
            : null);
        const targetUser: AuthUser = {
          id: String(result.user.id),
          name: result.user.name,
          email: result.user.email,
          account_type: result.user.accountType,
          role: result.user.role,
          is_platform_admin: result.user.isPlatformAdmin,
          is_staff: result.user.isStaff,
        };
        auth.setSession({
          user: targetUser,
          accessToken: result.access,
          refreshToken: result.refresh,
        });
        set({ origin, targetName: result.user.name, isImpersonating: true });
      },
      end: async () => {
        const { origin } = get();
        // Restore the operator's own session FIRST, so the stop call (and every
        // request after) is authenticated as the real operator again.
        if (origin) {
          useAuthStore.getState().setSession({
            user: origin.user,
            accessToken: origin.accessToken,
            refreshToken: origin.refreshToken,
          });
        }
        set({ origin: null, targetName: null, isImpersonating: false });
        try {
          await opsApi.stopImpersonation();
        } catch {
          // The local identity is already restored; a failed close is non-fatal.
        }
      },
    }),
    { name: "ops-impersonation" },
  ),
);

// When the real session is cleared — an explicit logout, or a failed token
// refresh that force-logs-out (lib/api) — abandon any impersonation immediately.
// begin()/end() go through setSession (isAuthenticated stays true), so this only
// fires on a genuine logout, never mid-swap. This is what stops a persisted
// `isImpersonating` from surviving a logout and hijacking the next user's login.
let wasAuthenticated = useAuthStore.getState().isAuthenticated;
useAuthStore.subscribe((state) => {
  const nowAuthenticated = state.isAuthenticated;
  if (wasAuthenticated && !nowAuthenticated) {
    const imp = useImpersonationStore.getState();
    if (imp.isImpersonating || imp.origin) imp.reset();
  }
  wasAuthenticated = nowAuthenticated;
});
